import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import { ViewList, Category } from "@material-ui/icons";
import { Add, Edit, Delete, Close, Save, Info } from "@material-ui/icons";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import CardBody from "components/Card/CardBody";
import FeedbackButton from "components/CTSGuide/FeedbackButton";
import { useTranslation } from "react-i18next";
import { client } from "services/axiosClient";
import PLTree from "components/CTSGuide/PLTree";
import ctsGuidePortalStyle from "assets/jss/material-dashboard-pro-react/views/ctsGuidePortalStyle";
import useThemeColors from "hooks/useThemeColors";
import { ThreeDots } from "react-loader-spinner";
import CTSMiniNav from "components/CTSGuide/CTSMiniNav";

const useStyles = makeStyles(ctsGuidePortalStyle);

export default function PLRegistration() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();
  const [activeTab, setActiveTab] = useState(0);
  const [plLines, setPlLines] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dimensionDialog, setDimensionDialog] = useState(false);
  const [editingDimension, setEditingDimension] = useState(null);
  const [dimensionForm, setDimensionForm] = useState({
    name: "",
    dimension_type: "product",
    source_file: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Single optimized call for all page data
      const res = await client.get("/api/ctsguide/pl-registration-data/");
      setPlLines(res.data.pl_lines || []);
      setDimensions(res.data.dimensions || []);
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  };

  // P&L Line handlers
  const handleAddPLLine = async (lineData) => {
    try {
      await client.post("/api/ctsguide/pl-lines/", lineData);
      loadData();
    } catch (error) {
      console.error("Error adding P&L line:", error);
    }
  };

  const handleEditPLLine = async (lineId, lineData) => {
    try {
      await client.put(`/api/ctsguide/pl-lines/${lineId}/`, lineData);
      loadData();
    } catch (error) {
      console.error("Error editing P&L line:", error);
    }
  };

  const handleDeletePLLine = async (lineId) => {
    if (!window.confirm(t("ctsguide.pl.confirm_delete"))) return;

    try {
      await client.delete(`/api/ctsguide/pl-lines/${lineId}/`);
      loadData();
    } catch (error) {
      console.error("Error deleting P&L line:", error);
    }
  };

  // Dimension handlers - simplified
  const handleAddDimension = () => {
    setEditingDimension(null);
    setDimensionForm({
      name: "",
      dimension_type: "product",
      source_file: "",
    });
    setDimensionDialog(true);
  };

  const handleEditDimension = (dimension) => {
    setEditingDimension(dimension);
    setDimensionForm({
      name:
        dimension.display_name?.pt ||
        dimension.display_name?.en ||
        dimension.meta_name ||
        "",
      dimension_type: dimension.dimension_type,
      source_file: dimension.source_file || "",
    });
    setDimensionDialog(true);
  };

  const handleSaveDimension = async () => {
    try {
      // Convert simplified form to API format
      const apiData = {
        meta_name: dimensionForm.name.toLowerCase().replace(/\s+/g, "_"),
        display_name: {
          en: dimensionForm.name,
          es: dimensionForm.name,
          pt: dimensionForm.name,
        },
        dimension_type: dimensionForm.dimension_type,
        source_file: dimensionForm.source_file || null,
        is_channel_reference: false,
        is_category_reference: false,
        is_region_reference: false,
      };

      if (editingDimension) {
        await client.put(
          `/api/ctsguide/dimensions/${editingDimension.id}/`,
          apiData,
        );
      } else {
        await client.post("/api/ctsguide/dimensions/", apiData);
      }
      setDimensionDialog(false);
      loadData();
    } catch (error) {
      console.error("Error saving dimension:", error);
    }
  };

  const handleDeleteDimension = async (dimensionId) => {
    if (!window.confirm(t("ctsguide.dimensions.confirm_delete"))) return;

    try {
      await client.delete(`/api/ctsguide/dimensions/${dimensionId}/`);
      loadData();
    } catch (error) {
      console.error("Error deleting dimension:", error);
    }
  };

  const getDimensionTypeBadge = (type) => {
    const colors = {
      product: { bg: "#e3f2fd", text: "#1565c0" },
      customer: { bg: "#f3e5f5", text: "#6a1b9a" },
      geography: { bg: "#e8f5e9", text: "#2e7d32" },
      commercial: { bg: "#fff3e0", text: "#e65100" },
      other: { bg: "#f5f5f5", text: "#666" },
    };
    const color = colors[type] || colors.other;
    return (
      <Chip
        label={t(`ctsguide.dimension_types.${type}`)}
        size="small"
        style={{ backgroundColor: color.bg, color: color.text }}
      />
    );
  };

  const getDimensionDisplayName = (dim) => {
    return dim.display_name?.pt || dim.display_name?.en || dim.meta_name || "-";
  };

  return (
    <div className={classes.pageContainer}>
      <Box display="flex" justifyContent="center" mb={0.5}>
        <CTSMiniNav />
      </Box>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="client" icon>
              <CardIcon color="client">
                <AccountTreeIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                {t("ctsguide.nav.pl_registration")}
              </h4>
              <div className={classes.headerTabsRight}>
                <button
                  type="button"
                  className={`${classes.headerTab} ${activeTab === 0 ? classes.headerTabActive : ""}`}
                  onClick={() => setActiveTab(0)}
                >
                  <ViewList className={classes.headerTabIcon} />
                  {t("ctsguide.pl.structure_tab")}
                </button>
                <button
                  type="button"
                  className={`${classes.headerTab} ${activeTab === 1 ? classes.headerTabActive : ""}`}
                  onClick={() => setActiveTab(1)}
                >
                  <Category className={classes.headerTabIcon} />
                  {t("ctsguide.pl.dimensions_tab")}
                </button>
              </div>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className={classes.loadingContainer}>
                  <ThreeDots
                    width="120px"
                    color={clientColor}
                    ariaLabel="loading"
                  />
                </div>
              ) : (
                <>
                  {/* P&L Structure Tab */}
                  {activeTab === 0 && (
                    <Box py={2}>
                      <p
                        style={{
                          marginBottom: "20px",
                          color: "#666",
                          fontSize: "14px",
                        }}
                      >
                        {t("ctsguide.pl.structure_description")}
                      </p>

                      <PLTree
                        lines={plLines}
                        onAdd={handleAddPLLine}
                        onEdit={handleEditPLLine}
                        onDelete={handleDeletePLLine}
                      />
                    </Box>
                  )}

                  {/* Dimensions Tab */}
                  {activeTab === 1 && (
                    <Box py={2}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <p
                          style={{
                            color: "#666",
                            fontSize: "14px",
                            margin: 0,
                          }}
                        >
                          {t("ctsguide.dimensions.description")}
                        </p>
                        <Tooltip title={t("ctsguide.dimensions.add")}>
                          <span>
                            <Button
                              size="sm"
                              justIcon
                              color="client"
                              onClick={handleAddDimension}
                            >
                              <Add />
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>

                      {dimensions.length > 0 ? (
                        <Table className={classes.inputsTable}>
                          <TableHead className={classes.inputsTableHeader}>
                            <TableRow>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                              >
                                {t("ctsguide.dimensions.name")}
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                              >
                                {t("ctsguide.dimensions.type")}
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                              >
                                {t("ctsguide.dimensions.source_file")}
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                                style={{ width: "100px" }}
                              >
                                {t("common.actions")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dimensions.map((dim) => (
                              <TableRow key={dim.id} hover>
                                <TableCell className={classes.inputsTableCell}>
                                  {getDimensionDisplayName(dim)}
                                </TableCell>
                                <TableCell className={classes.inputsTableCell}>
                                  {getDimensionTypeBadge(dim.dimension_type)}
                                </TableCell>
                                <TableCell className={classes.inputsTableCell}>
                                  <span
                                    style={{
                                      color: dim.source_file ? "#333" : "#999",
                                    }}
                                  >
                                    {dim.source_file || "-"}
                                  </span>
                                </TableCell>
                                <TableCell className={classes.inputsTableCell}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditDimension(dim)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDeleteDimension(dim.id)
                                    }
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Box textAlign="center" p={4} style={{ color: "#999" }}>
                          <p>{t("ctsguide.dimensions.no_dimensions")}</p>
                          <Tooltip title={t("ctsguide.dimensions.add_first")}>
                            <span>
                              <Button
                                size="sm"
                                justIcon
                                color="client"
                                style={{ marginTop: "10px" }}
                                onClick={handleAddDimension}
                              >
                                <Add />
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Dimension Dialog - Simplified */}
      <Dialog
        open={dimensionDialog}
        onClose={() => setDimensionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <ModalHeader
          title={
            editingDimension
              ? t("ctsguide.dimensions.edit")
              : t("ctsguide.dimensions.add")
          }
          onClose={() => setDimensionDialog(false)}
          closeLabel={t("common.close")}
          clientColor={clientColor}
        />
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            label={t("ctsguide.dimensions.name")}
            value={dimensionForm.name}
            onChange={(e) =>
              setDimensionForm({ ...dimensionForm, name: e.target.value })
            }
            margin="normal"
            variant="outlined"
            placeholder={t("ctsguide.dimensions.name_placeholder")}
            onKeyPress={(e) => {
              if (e.key === "Enter" && dimensionForm.name) {
                handleSaveDimension();
              }
            }}
          />

          <TextField
            select
            fullWidth
            label={t("ctsguide.dimensions.type")}
            value={dimensionForm.dimension_type}
            onChange={(e) =>
              setDimensionForm({
                ...dimensionForm,
                dimension_type: e.target.value,
              })
            }
            margin="normal"
            variant="outlined"
          >
            <MenuItem value="product">
              {t("ctsguide.dimension_types.product")}
            </MenuItem>
            <MenuItem value="customer">
              {t("ctsguide.dimension_types.customer")}
            </MenuItem>
            <MenuItem value="geography">
              {t("ctsguide.dimension_types.geography")}
            </MenuItem>
            <MenuItem value="commercial">
              {t("ctsguide.dimension_types.commercial")}
            </MenuItem>
            <MenuItem value="other">
              {t("ctsguide.dimension_types.other")}
            </MenuItem>
          </TextField>

          <Box display="flex" alignItems="center" mt={2}>
            <TextField
              fullWidth
              label={t("ctsguide.dimensions.source_file")}
              value={dimensionForm.source_file}
              onChange={(e) =>
                setDimensionForm({
                  ...dimensionForm,
                  source_file: e.target.value,
                })
              }
              variant="outlined"
              placeholder={t("ctsguide.dimensions.source_file_placeholder")}
            />
            <Tooltip title={t("ctsguide.dimensions.source_file_hint")}>
              <IconButton size="small" style={{ marginLeft: "8px" }}>
                <Info fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Tooltip title={t("common.cancel")}>
            <span>
              <Button
                size="sm"
                justIcon
                color="system_gray"
                onClick={() => setDimensionDialog(false)}
              >
                <Close />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t("common.save")}>
            <span>
              <Button
                size="sm"
                justIcon
                color="client"
                onClick={handleSaveDimension}
                disabled={!dimensionForm.name}
              >
                <Save />
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Floating Feedback Button */}
      <FeedbackButton pageOrigin="plRegistration" />
    </div>
  );
}
